import { Content, ContentSubType } from '@amzn/awscat-pattern-library-service-model';

import { getContentTemplateSchema, getCurrentTemplateInfo } from '../api/UtilsApi';
import { deepCloneContent } from '../model/CloneContent';
import { ContentSchemaSection } from '../model/ContentSchemaSection';
import { ContentUIComponent } from '../model/Enum';
import { resolveWorkloadSelection } from '../components/utils/SelectableOptions';

/** Raw shape of one entry in a template's `templateMetadata.properties`. */
interface TemplatePropertySchema {
  readonly label?: string;
  readonly description?: string;
  readonly UIComponent: ContentUIComponent;
  readonly maxNumber?: number;
}

/**
 * One entry in the schema-driven Configuration form. The view walks this
 * list and renders an input per entry; this type hides the raw JSON-schema
 * shape so callers don't read template properties off `any`. Pass the same
 * object back to {@link setMetadataField} to update its value.
 */
export interface MetadataField {
  /** Stable key on `templateMetadata` (e.g. `mainTechDomain`, `awsServices`). */
  readonly key: string;
  /** Human-readable label shown next to the input. */
  readonly label: string;
  /** Help text shown below the label, when the schema provides one. */
  readonly description: string | undefined;
  /** When true, the view should display a "required" affordance and validation surfaces an error if empty. */
  readonly isRequired: boolean;
  /** Tag identifying which input control to render (string / select / multiselect / checkbox). */
  readonly uiComponent: ContentUIComponent;
  /** Multiselect cap (`undefined` = no cap). Sourced from the schema's `maxNumber`. */
  readonly maxNumber: number | undefined;
  /** Current value bound to the input, normalized for the matching UI component. */
  readonly value: unknown;
}

/**
 * Builds the schema-driven list of metadata fields for a content. Filters
 * out `ContentDataSection` entries since those are body sections, not
 * metadata fields. Returns an empty list when the content has no template.
 */
export function getMetadataFields(content: Content): readonly MetadataField[] {
  const templateSchema = getContentTemplateSchema(
    content.contentMetadata.contentType,
    content.contentTypeMetadata,
    ContentSchemaSection.TemplateMetadata,
  );
  if (!templateSchema?.properties) return [];

  const required: readonly string[] = templateSchema.required ?? [];
  const templateMetadata = (content.templateMetadata ?? {}) as Record<string, unknown>;
  const properties = templateSchema.properties as Record<string, TemplatePropertySchema>;

  return Object.entries(properties)
    .filter(([, prop]) => prop.UIComponent !== ContentUIComponent.ContentDataSection)
    .map(
      ([key, prop]): MetadataField => ({
        key,
        label: prop.label ?? key,
        description: prop.description,
        isRequired: required.includes(key),
        uiComponent: prop.UIComponent,
        maxNumber: prop.maxNumber,
        value: templateMetadata[key],
      }),
    );
}

/**
 * Validates a candidate value for `field` against its `isRequired` flag.
 * Empty arrays, empty strings, `null`, and `undefined` count as empty.
 */
export function validateMetadataField(field: MetadataField, value: unknown): string | undefined {
  if (!field.isRequired) return undefined;
  const isEmpty =
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);
  return isEmpty ? 'This field is required.' : undefined;
}

/**
 * Returns a clone of `content` with the value at `field.key` set to
 * `value`. Workload values run through `resolveWorkloadSelection` to handle
 * the parent/child cascade legacy uses. Pass the same `MetadataField`
 * object returned by {@link getMetadataFields}.
 */
export function setMetadataField(content: Content, field: MetadataField, value: unknown): Content {
  const previousMetadata = (content.templateMetadata ?? {}) as Record<string, unknown>;
  const previousWorkload = previousMetadata[field.key];
  const next =
    field.key === 'workload' && Array.isArray(value)
      ? resolveWorkloadSelection(
          value as string[],
          Array.isArray(previousWorkload) ? (previousWorkload as string[]) : [],
        )
      : value;

  const updated = deepCloneContent(content);
  updated.templateMetadata = { ...(updated.templateMetadata ?? {}), [field.key]: next };
  return updated;
}

/**
 * Returns a clone of `content` with `contentTypeMetadata.contentSubType`
 * set to `subType`. Also refreshes `templateId`/`templateVersion` so the
 * schema-driven field list reacts to the sub-type change — without this,
 * an existing draft keeps its prior `templateId` and the configuration
 * form would not refresh.
 */
export function setContentSubType(content: Content, subType: ContentSubType): Content {
  const { templateId, templateVersion } = getCurrentTemplateInfo(subType);

  const updated = deepCloneContent(content);
  const ctm = updated.contentTypeMetadata;
  ctm.contentSubType = subType;
  ctm.templateId = templateId;
  ctm.templateVersion = templateVersion;
  return updated;
}
